import {
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  Heading,
  HStack,
  IconButton,
  Input,
  InputGroup,
  InputLeftAddon,
  InputRightAddon,
  Radio,
  Switch,
  Table,
  TableCaption,
  TableContainer,
  Tbody,
  Td,
  Text,
  Tfoot,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import { Trans, t } from "@lingui/macro";
import {
  FormRow,
  ModalConfirm,
  Select,
  TextField,
} from "@src/components/ui-kit";
import { Icon } from "@src/components/ui-kit/Icon";
import { useStore } from "@src/utils/hooks";
import { observer } from "mobx-react-lite";
import { Fragment, type FunctionComponent } from "react";

export const Financial: FunctionComponent = observer(function Financial() {
  const {
    settingsModalStore: { financialStore },
    workspaceStore,
  } = useStore();

  return (
    <Fragment>
      <TableContainer>
        <Table size="sm" variant="simple">
          <TableCaption paddingInlineStart="0" placement="top">
            <Heading as="h3" textAlign="left" size="md">
              <Trans>VAT rates</Trans>
            </Heading>
          </TableCaption>
          <Thead>
            <Tr>
              <Th textAlign="center">
                <Trans>Default</Trans>
              </Th>
              <Th w="99%">
                <Trans>Amount</Trans>
              </Th>
              <Th textAlign="center">
                <Trans>Actions</Trans>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {financialStore.form.form.$.vatRates.$.map((vatRate) => (
              <Tr key={vatRate.internalId}>
                <Td>
                  <Flex as="label" align="center" justify="center">
                    <Radio
                      isChecked={vatRate.$.default.value}
                      name="default"
                      onChange={(event) => {
                        if (event.target.checked) {
                          financialStore.form.makeDefault(vatRate);
                        }
                      }}
                    />
                  </Flex>
                </Td>
                <Td>
                  <FormControl isInvalid={vatRate.$.amount.hasError}>
                    <Input
                      inputMode="decimal"
                      isDisabled={Boolean(vatRate.id)}
                      onChange={(event) =>
                        vatRate.$.amount.onChange(event.target.value)
                      }
                      type="number"
                      value={vatRate.$.amount.value}
                    />
                    <FormErrorMessage>
                      <Trans>VAT rate amount is required</Trans>
                    </FormErrorMessage>
                  </FormControl>
                </Td>
                <Td>
                  <Flex align="center" justify="center" gap="3">
                    <Tooltip
                      hasArrow
                      label={
                        financialStore.form.form.$.vatRates.$.length === 1
                          ? t`You cannot remove the last VAT rate`
                          : t`Remove this VAT rate`
                      }
                    >
                      <IconButton
                        aria-label={t`Delete this VAT rate`}
                        icon={<Icon name="trash-03" />}
                        isDisabled={
                          financialStore.form.form.$.vatRates.$.length === 1
                        }
                        onClick={() =>
                          financialStore.form.requestVatRateRemoval(vatRate)
                        }
                        variant="ghost"
                      />
                    </Tooltip>
                    <ModalConfirm
                      heading={t`Confirm removal of VAT rate`}
                      onClose={() =>
                        (financialStore.form.vatRateToDelete = undefined)
                      }
                      isOpen={financialStore.form.vatRateToDelete === vatRate}
                      onConfirm={() =>
                        financialStore.form.removeVatRate(vatRate)
                      }
                    >
                      <Trans>
                        Removal of this VAT rate will keep past uses untouched,
                        but you will not be able to use it in the future
                        anymore. Do you want to remove this VAT rate?
                      </Trans>
                    </ModalConfirm>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
          <Tfoot>
            <Tr>
              <Th colSpan={3}>
                <Flex justify="end" paddingBlock="3">
                  <Button
                    leftIcon={<Icon name="plus" />}
                    onClick={() => financialStore.form.addNewVatRate()}
                    variant="outline"
                  >
                    <Trans>Add new VAT rate</Trans>
                  </Button>
                </Flex>
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>
      <TableContainer>
        <Table size="sm" variant="simple">
          <TableCaption paddingInlineStart="0" placement="top">
            <Heading as="h3" textAlign="left" size="md">
              <Trans>Currencies & exchange rates</Trans>
            </Heading>
          </TableCaption>
          <Thead>
            <Tr>
              <Th>
                <Trans>Currency</Trans>
              </Th>
              <Th textAlign="center">
                <Trans>Automatic from ECB</Trans>
              </Th>
              <Th w="99%">
                <Trans>Manual exchange rate</Trans>
              </Th>
              <Th textAlign="center">
                <Trans>Actions</Trans>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {financialStore.form.form.$.currencies.$.map((currency) => (
              <Tr key={currency.internalId}>
                <Td>
                  <Tooltip
                    isDisabled={currency.canBeRemoved}
                    label={t`Saved currency can't be changed.`}
                  >
                    <Flex as="label" align="center" justify="center" w="90px">
                      <Select
                        isDisabled={!currency.canBeRemoved}
                        value={currency.$.code.value}
                        onChange={currency.$.code.onChange}
                        error={currency.$.code.error}
                        options={financialStore.form.getFilteredCurrencyOptions(
                          currency.$.code.value,
                        )}
                      />
                    </Flex>
                  </Tooltip>
                </Td>
                {currency.$.code.$ !==
                workspaceStore.settings?.currency.code ? (
                  <>
                    <Td>
                      <Flex align="center" justify="center">
                        <Checkbox
                          isChecked={currency.$.isAuto.value}
                          name="default"
                          onChange={({ target }) => {
                            currency.handleSetAuto(target.checked);
                          }}
                        />
                      </Flex>
                    </Td>
                    <Td>
                      <InputGroup>
                        <InputLeftAddon>
                          1&nbsp;{workspaceStore.settings?.currency.code}&nbsp;=
                        </InputLeftAddon>
                        <Input
                          inputMode="decimal"
                          isDisabled={currency.isExchangeRateInputDisabled}
                          onChange={({ target }) =>
                            currency.$.exchange_rate.onChange(target.value)
                          }
                          placeholder={
                            currency.isExchangeRateInputDisabled
                              ? t`Updated daily`
                              : t`Manual exchange rate`
                          }
                          type="number"
                          value={currency.$.exchange_rate.value}
                        />
                        <InputRightAddon>
                          {currency.$.code.value}
                        </InputRightAddon>
                      </InputGroup>
                    </Td>
                  </>
                ) : (
                  <>
                    <Td colSpan={2}>
                      <Text color="gray" textAlign="center">
                        <Trans>System currency can't be changed</Trans>
                      </Text>
                    </Td>
                  </>
                )}
                <Td>
                  <Flex align="center" justify="center" gap="3">
                    <Tooltip
                      hasArrow
                      label={
                        currency.canBeRemoved
                          ? t`Remove this currency`
                          : t`Can't remove already saved currency.`
                      }
                    >
                      <IconButton
                        aria-label={t`Delete this currency`}
                        icon={<Icon name="trash-03" />}
                        isDisabled={!currency.canBeRemoved}
                        onClick={() => {
                          financialStore.form.removeCurrency(
                            currency.internalId,
                          );
                        }}
                        variant="ghost"
                      />
                    </Tooltip>
                  </Flex>
                </Td>
              </Tr>
            ))}
          </Tbody>
          <Tfoot>
            <Tr>
              <Td colSpan={4}>
                <HStack justify="space-between" paddingBlock="3">
                  <Text color="gray">
                    <Trans>
                      The exchange rate set in the client detail takes
                      precedence over any rates set above.
                    </Trans>
                  </Text>
                  <Button
                    leftIcon={<Icon name="plus" />}
                    onClick={() => financialStore.form.addNewCurrency()}
                    variant="outline"
                  >
                    <Trans>Add new currency</Trans>
                  </Button>
                </HStack>
              </Td>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>
      <TableContainer>
        <Table size="sm" variant="simple">
          <TableCaption paddingInlineStart="0" placement="top">
            <Heading as="h3" textAlign="left" size="md">
              <Trans>Revenue account classification</Trans>
            </Heading>
          </TableCaption>
          <Thead>
            <Tr>
              <Th minW="240px">
                <Trans>Number</Trans>
              </Th>
              <Th w="99%">
                <Trans>Title</Trans>
              </Th>
              <Th minW="180px" textAlign="center">
                <Trans>Default</Trans>
              </Th>
              <Th textAlign="center">
                <Trans>Actions</Trans>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {financialStore.form.form.$.billingAccountingAccounts.$.map(
              (account) => (
                <Tr key={account.internalId}>
                  <Td verticalAlign="top">
                    <FormControl isInvalid={account.$.account_number.hasError}>
                      <Input
                        onChange={(event) =>
                          account.$.account_number.onChange(event.target.value)
                        }
                        type="text"
                        value={account.$.account_number.value}
                      />
                      <FormErrorMessage>
                        {account.$.account_number.error}
                      </FormErrorMessage>
                    </FormControl>
                  </Td>
                  <Td verticalAlign="top">
                    <FormControl isInvalid={account.$.title.hasError}>
                      <Input
                        onChange={(event) =>
                          account.$.title.onChange(event.target.value)
                        }
                        type="text"
                        value={account.$.title.value}
                      />
                      <FormErrorMessage>
                        <Trans>Title is required</Trans>
                      </FormErrorMessage>
                    </FormControl>
                  </Td>
                  <Td verticalAlign="top">
                    <Select
                      isMulti
                      value={account.$.budget_categories.value}
                      options={financialStore.form.budgetCategoryOptions}
                      onChange={account.$.budget_categories.onChange}
                      asPortal
                    />
                  </Td>
                  <Td>
                    <Flex align="center" justify="center" gap="3">
                      <Tooltip
                        hasArrow
                        label={
                          financialStore.form.form.$.billingAccountingAccounts.$
                            .length === 1
                            ? t`You cannot remove the last account`
                            : t`Remove this account`
                        }
                      >
                        <IconButton
                          aria-label={t`Delete this account`}
                          icon={<Icon name="trash-03" />}
                          isDisabled={
                            financialStore.form.form.$.billingAccountingAccounts
                              .$.length === 1
                          }
                          onClick={() =>
                            financialStore.form.requestBillingAccountingAccountRemoval(
                              account,
                            )
                          }
                          variant="ghost"
                        />
                      </Tooltip>
                      <ModalConfirm
                        heading={t`Confirm removal of accounting account`}
                        onClose={() =>
                          (financialStore.form.billingAccountingAccountToDelete =
                            undefined)
                        }
                        isOpen={
                          financialStore.form
                            .billingAccountingAccountToDelete === account
                        }
                        onConfirm={() =>
                          financialStore.form.removeBillingAccountingAccount(
                            account,
                          )
                        }
                      >
                        <Trans>
                          Removal of this accounting account will keep past uses
                          untouched, but you will not be able to use it in the
                          future anymore. Do you want to remove this account?
                        </Trans>
                      </ModalConfirm>
                    </Flex>
                  </Td>
                </Tr>
              ),
            )}
          </Tbody>
          <Tfoot>
            <Tr>
              <Th colSpan={3}>
                <Flex justify="end" paddingBlock="3">
                  <Button
                    leftIcon={<Icon name="plus" />}
                    onClick={() =>
                      financialStore.form.addNewBillingAccountingAccount()
                    }
                    variant="outline"
                  >
                    <Trans>Add new accounting account</Trans>
                  </Button>
                </Flex>
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>
      <TableContainer>
        <Table size="sm" variant="simple">
          <TableCaption paddingInlineStart="0" placement="top">
            <Heading as="h3" textAlign="left" size="md">
              <Trans>Expenses account classification</Trans>
            </Heading>
          </TableCaption>
          <Thead>
            <Tr>
              <Th minW="280px">
                <Trans>Number</Trans>
              </Th>
              <Th w="99%">
                <Trans>Title</Trans>
              </Th>
              <Th textAlign="center">
                <Trans>Actions</Trans>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {financialStore.form.form.$.expensesAccountingAccounts.$.map(
              (account) => (
                <Tr key={account.internalId}>
                  <Td verticalAlign="top">
                    <FormControl isInvalid={account.$.account_number.hasError}>
                      <Input
                        onChange={(event) =>
                          account.$.account_number.onChange(event.target.value)
                        }
                        type="text"
                        value={account.$.account_number.value}
                      />
                      <FormErrorMessage>
                        {account.$.account_number.error}
                      </FormErrorMessage>
                    </FormControl>
                  </Td>
                  <Td verticalAlign="top">
                    <FormControl isInvalid={account.$.title.hasError}>
                      <Input
                        onChange={(event) =>
                          account.$.title.onChange(event.target.value)
                        }
                        type="text"
                        value={account.$.title.value}
                      />
                      <FormErrorMessage>
                        <Trans>Title is required</Trans>
                      </FormErrorMessage>
                    </FormControl>
                  </Td>
                  <Td>
                    <Flex align="center" justify="center" gap="3">
                      <Tooltip
                        hasArrow
                        label={
                          financialStore.form.form.$.expensesAccountingAccounts
                            .$.length === 1
                            ? t`You cannot remove the last account`
                            : t`Remove this account`
                        }
                      >
                        <IconButton
                          aria-label={t`Delete this account`}
                          icon={<Icon name="trash-03" />}
                          isDisabled={
                            financialStore.form.form.$
                              .expensesAccountingAccounts.$.length === 1
                          }
                          onClick={() =>
                            financialStore.form.requestExpensesAccountingAccountRemoval(
                              account,
                            )
                          }
                          variant="ghost"
                        />
                      </Tooltip>
                      <ModalConfirm
                        heading={t`Confirm removal of accounting account`}
                        onClose={() =>
                          (financialStore.form.expensesAccountingAccountToDelete =
                            undefined)
                        }
                        isOpen={
                          financialStore.form
                            .expensesAccountingAccountToDelete === account
                        }
                        onConfirm={() =>
                          financialStore.form.removeExpensesAccountingAccount(
                            account,
                          )
                        }
                      >
                        <Trans>
                          Removal of this accounting account will keep past uses
                          untouched, but you will not be able to use it in the
                          future anymore. Do you want to remove this account?
                        </Trans>
                      </ModalConfirm>
                    </Flex>
                  </Td>
                </Tr>
              ),
            )}
          </Tbody>
          <Tfoot>
            <Tr>
              <Th colSpan={3}>
                <Flex justify="end" paddingBlock="3">
                  <Button
                    leftIcon={<Icon name="plus" />}
                    onClick={() =>
                      financialStore.form.addNewExpensesAccountingAccount()
                    }
                    variant="outline"
                  >
                    <Trans>Add new accounting account</Trans>
                  </Button>
                </Flex>
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>
      <TableContainer>
        <Table size="sm" variant="simple">
          <TableCaption paddingInlineStart="0" placement="top">
            <Heading as="h3" textAlign="left" size="md">
              <Trans>Cost centers</Trans>
            </Heading>
          </TableCaption>
          <Thead>
            <Tr>
              <Th minW="180px">
                <Trans>Type</Trans>
              </Th>
              <Th minW="280px">
                <Trans>Cost center</Trans>
              </Th>
              <Th w="99%">
                <Trans>Activity</Trans>
              </Th>
              <Th textAlign="center">
                <Trans>Actions</Trans>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {financialStore.form.form.$.costCenters.$.length ? (
              financialStore.form.form.$.costCenters.$.map((costCenter) => (
                <Tr key={costCenter.internalId}>
                  <Td verticalAlign="top">
                    <Select
                      asPortal
                      value={costCenter.$.type.value}
                      onChange={costCenter.$.type.onChange}
                      error={costCenter.$.type.error}
                      options={financialStore.form.costCenterOptions}
                    />
                  </Td>
                  <Td verticalAlign="top">
                    <TextField
                      placeholder={t`Cost center`}
                      value={costCenter.$.cost_center.value}
                      onChange={costCenter.$.cost_center.onChange}
                      error={costCenter.$.cost_center.error}
                    />
                  </Td>
                  <Td verticalAlign="top">
                    <TextField
                      placeholder={t`Activity`}
                      value={costCenter.$.activity.value}
                      onChange={costCenter.$.activity.onChange}
                      error={costCenter.$.activity.error}
                    />
                  </Td>
                  <Td>
                    <Flex align="center" justify="center" gap="3">
                      <Tooltip
                        hasArrow
                        label={
                          financialStore.form.form.$.costCenters.$.length === 1
                            ? t`You cannot remove the last cost center`
                            : t`Remove this cost center`
                        }
                      >
                        <IconButton
                          aria-label={t`Delete this cost center`}
                          icon={<Icon name="trash-03" />}
                          isDisabled={
                            financialStore.form.form.$.costCenters.$.length ===
                            1
                          }
                          onClick={() =>
                            financialStore.form.requestCostCenterRemoval(
                              costCenter,
                            )
                          }
                          variant="ghost"
                        />
                      </Tooltip>
                      <ModalConfirm
                        heading={t`Confirm removal of cost center`}
                        onClose={() =>
                          (financialStore.form.costCenterToDelete = undefined)
                        }
                        isOpen={
                          financialStore.form.costCenterToDelete === costCenter
                        }
                        onConfirm={() =>
                          financialStore.form.removeCostCenter(costCenter)
                        }
                      >
                        <Trans>
                          Removal of this cost center will keep past uses
                          untouched, but you will not be able to use it in the
                          future anymore. Do you want to remove this cost
                          center?
                        </Trans>
                      </ModalConfirm>
                    </Flex>
                  </Td>
                </Tr>
              ))
            ) : (
              <Tr>
                <Td textAlign="center" colSpan={4}>
                  <Text py="2" color="gray.500">
                    <Trans>No cost centers created yet.</Trans>
                  </Text>
                </Td>
              </Tr>
            )}
          </Tbody>
          <Tfoot>
            <Tr>
              <Th colSpan={4}>
                <Flex justify="end" paddingBlock="3">
                  <Button
                    leftIcon={<Icon name="plus" />}
                    onClick={financialStore.form.addNewCostCenter}
                    variant="outline"
                  >
                    <Trans>Add new cost center</Trans>
                  </Button>
                </Flex>
              </Th>
            </Tr>
          </Tfoot>
        </Table>
      </TableContainer>
      {workspaceStore.settings?.show_accounting_vat_classification && (
        <TableContainer>
          <Table size="sm" variant="simple">
            <TableCaption paddingInlineStart="0" placement="top">
              <Heading as="h3" textAlign="left" size="md">
                <Trans>Accounting VAT classification</Trans>
              </Heading>
            </TableCaption>
            <Thead>
              <Tr>
                {/* <Th minW="180px">
                  <Trans>Type</Trans>
                </Th> */}
                <Th minW="180px">
                  <Trans>VAT Rate</Trans>
                </Th>
                <Th minW="140px">
                  <Trans>Centre</Trans>
                </Th>
                <Th w="99%">
                  <Trans>Title</Trans>
                </Th>
                <Th textAlign="center">
                  <Trans>Actions</Trans>
                </Th>
              </Tr>
            </Thead>
            <Tbody>
              {financialStore.form.form.$.accountingVatClassifications.$
                .length ? (
                financialStore.form.form.$.accountingVatClassifications.$.map(
                  (classification) => (
                    <Tr key={classification.internalId}>
                      {/* <Td verticalAlign="top">
                        <Select
                          asPortal
                          value={classification.$.type.value}
                          onChange={classification.$.type.onChange}
                          error={classification.$.type.error}
                          options={financialStore.form.costCenterOptions}
                        />
                      </Td> */}
                      <Td verticalAlign="top">
                        <Select
                          asPortal
                          value={classification.$.vat_rate_id.value}
                          onChange={classification.$.vat_rate_id.onChange}
                          error={classification.$.vat_rate_id.error}
                          options={financialStore.form.vatRateOptions}
                        />
                      </Td>
                      <Td verticalAlign="top">
                        <TextField
                          placeholder={t`Centre`}
                          value={classification.$.centre.value}
                          onChange={classification.$.centre.onChange}
                          error={classification.$.centre.error}
                        />
                      </Td>
                      <Td verticalAlign="top">
                        <TextField
                          placeholder={t`Title`}
                          value={classification.$.title.value}
                          onChange={classification.$.title.onChange}
                          error={classification.$.title.error}
                        />
                      </Td>
                      <Td>
                        <Flex align="center" justify="center" gap="3">
                          <Tooltip
                            hasArrow
                            label={
                              financialStore.form.form.$
                                .accountingVatClassifications.$.length === 1
                                ? t`You cannot remove the last VAT classification`
                                : t`Remove this VAT classification`
                            }
                          >
                            <IconButton
                              aria-label={t`Delete this VAT classification`}
                              icon={<Icon name="trash-03" />}
                              isDisabled={
                                financialStore.form.form.$
                                  .accountingVatClassifications.$.length === 1
                              }
                              onClick={() =>
                                financialStore.form.requestAccountingVatClassificationRemoval(
                                  classification,
                                )
                              }
                              variant="ghost"
                            />
                          </Tooltip>
                          <ModalConfirm
                            heading={t`Confirm removal of VAT classification`}
                            onClose={() =>
                              (financialStore.form.accountingVatClassificationToDelete =
                                undefined)
                            }
                            isOpen={
                              financialStore.form
                                .accountingVatClassificationToDelete ===
                              classification
                            }
                            onConfirm={() =>
                              financialStore.form.removeAccountingVatClassification(
                                classification,
                              )
                            }
                          >
                            <Trans>
                              Removal of this VAT classification will keep past
                              uses untouched, but you will not be able to use it
                              in the future anymore. Do you want to remove this
                              VAT classification?
                            </Trans>
                          </ModalConfirm>
                        </Flex>
                      </Td>
                    </Tr>
                  ),
                )
              ) : (
                <Tr>
                  <Td textAlign="center" colSpan={5}>
                    <Text py="2" color="gray.500">
                      <Trans>No VAT classifications created yet.</Trans>
                    </Text>
                  </Td>
                </Tr>
              )}
            </Tbody>
            <Tfoot>
              <Tr>
                <Th colSpan={5}>
                  <Flex justify="end" paddingBlock="3">
                    <Button
                      leftIcon={<Icon name="plus" />}
                      onClick={
                        financialStore.form.addNewAccountingVatClassification
                      }
                      variant="outline"
                    >
                      <Trans>Add new VAT classification</Trans>
                    </Button>
                  </Flex>
                </Th>
              </Tr>
            </Tfoot>
          </Table>
        </TableContainer>
      )}
      <Heading as="h3" mb="2" textAlign="left" size="md">
        <Trans>Invoices options</Trans>
      </Heading>
      <FormRow title={<Trans>Show unit price on invoices by default</Trans>}>
        <Switch
          isChecked={financialStore.form.form.$.showUnitPrice.value}
          name="financial_show_unit_price"
          onChange={({ target }) =>
            financialStore.form.form.$.showUnitPrice.onChange(target.checked)
          }
        />
      </FormRow>
      <FormRow
        title={
          <Trans>Show account classification on invoices by default</Trans>
        }
      >
        <Switch
          isChecked={financialStore.form.form.$.showBillingAccounting.value}
          name="financial_billing_accounting"
          onChange={({ target }) =>
            financialStore.form.form.$.showBillingAccounting.onChange(
              target.checked,
            )
          }
        />
      </FormRow>
      <Heading as="h3" mt="6" mb="2" textAlign="left" size="md">
        <Trans>Invoice attachment name templates</Trans>
      </Heading>
      <Trans>
        <Text mb="1" color="gray.500" fontSize="sm">
          Configure the default file name for invoice PDF attachments sent via
          email. Available placeholders:
        </Text>
        <VStack align="start" mb="1" color="gray.500" fontSize="sm">
          <div>
            <code>{"#number#"}</code> — invoice number
          </div>
          <div>
            <code>{"#issued#"}</code> — issue date
          </div>
          <div>
            <code>{"#company#"}</code> — company name
          </div>
          <div>
            <code>{"#client#"}</code> — client name
          </div>
        </VStack>
        <Text mb="4" color="gray.500" fontSize="sm">
          Example:{" "}
          {`"#client#_#number#_#issued#" →
          "Company_2024-0001_2026-06-12.pdf"`}
        </Text>
      </Trans>
      <FormRow title={<Trans>Outgoing invoice</Trans>}>
        <Input
          onChange={({ target }) =>
            financialStore.form.form.$.attachmentNameOutgoingInvoice.onChange(
              target.value,
            )
          }
          placeholder={t`Outgoing Invoice - #number#`}
          value={financialStore.form.form.$.attachmentNameOutgoingInvoice.value}
        />
      </FormRow>
      <FormRow title={<Trans>Proforma invoice</Trans>}>
        <Input
          onChange={({ target }) =>
            financialStore.form.form.$.attachmentNameOutgoingProforma.onChange(
              target.value,
            )
          }
          placeholder={t`Proforma Invoice - #number#`}
          value={
            financialStore.form.form.$.attachmentNameOutgoingProforma.value
          }
        />
      </FormRow>
      <FormRow title={<Trans>Payment receipt</Trans>}>
        <Input
          onChange={({ target }) =>
            financialStore.form.form.$.attachmentNamePaymentReceipt.onChange(
              target.value,
            )
          }
          placeholder={t`Payment receipt - #number#`}
          value={financialStore.form.form.$.attachmentNamePaymentReceipt.value}
        />
      </FormRow>
      {workspaceStore.settings?.country.code === "CZ" && (
        <>
          <Heading as="h3" mb="2" textAlign="left" size="md">
            <Trans>Contractor options</Trans>
          </Heading>
          <FormRow title={<Trans>Show bank account number and code</Trans>}>
            <Switch
              isChecked={
                financialStore.form.form.$.show_full_bank_account_information
                  .value
              }
              name="financial_show_unit_price"
              onChange={({ target }) =>
                financialStore.form.form.$.show_full_bank_account_information.onChange(
                  target.checked,
                )
              }
            />
          </FormRow>
        </>
      )}
    </Fragment>
  );
});
